import { useCallback, useEffect, useRef, useState } from 'react';
import { CenterState, ErrorBanner, Screen, Spinner } from './components/Ui';
import { ApiError, api } from './lib/api';
import { getStartParam, isInsideTelegram, parseCollectionId, setBackButton } from './lib/telegram';
import type { Me } from './types';
import { CollectionView } from './views/CollectionView';
import { EditorView } from './views/EditorView';
import { HomeView } from './views/HomeView';
import { StatsView } from './views/StatsView';

type Route =
  | { name: 'home' }
  | { name: 'editor'; collectionId?: string }
  | { name: 'viewer'; collectionId: string }
  | { name: 'stats'; collectionId: string };

/** Приход из поста в канале: ?startapp=col_<id>. */
function startCollectionId(): string | undefined {
  return parseCollectionId(getStartParam());
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  /**
   * Корневой экран зависит от того, как приложение открыли.
   *
   * Пришёл по кнопке из канала — корень витрина подборки, и «назад» вести
   * некуда: подписчик должен уметь только закрыть мини-аппу, а не попасть
   * в редактор и настройки каналов автора.
   */
  const rootRoute = useRef<Route>(
    ((): Route => {
      const collectionId = startCollectionId();
      return collectionId ? { name: 'viewer', collectionId } : { name: 'home' };
    })(),
  );
  const [route, setRoute] = useState<Route>(rootRoute.current);

  /**
   * Загрузка профиля вынесена в функцию, чтобы её можно было повторить.
   *
   * Без этого один сорвавшийся запрос намертво запирал авторскую часть:
   * показывалась ошибка, и выйти из неё можно было только закрыв и открыв
   * приложение заново. На мобильной сети через VPN это случается регулярно.
   */
  const loadMe = useCallback(async () => {
    setError(null);
    try {
      setMe(await api.me());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? { code: err.code, message: err.message }
          : { code: 'UNKNOWN', message: 'Не удалось связаться с сервером' },
      );
    }
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  // Системная кнопка «назад» — только когда есть куда возвращаться.
  // На корневом экране её нет, там работает штатное «Закрыть» Telegram.
  useEffect(() => {
    const root = rootRoute.current;
    const isRoot =
      route.name === root.name &&
      (route.name !== 'viewer' || route.collectionId === (root as { collectionId: string }).collectionId);
    return setBackButton(!isRoot, () => setRoute(root));
  }, [route]);

  // Витрину подборки показываем, не дожидаясь /api/me — это путь подписчика,
  // ему важна скорость, а не профиль. Свои ошибки она показывает сама.
  if (route.name === 'viewer') {
    return (
      // Витрина ничего не предлагает, кроме перехода в магазин: правки
      // делаются в редакторе, куда попадают со списка подборок.
      <CollectionView collectionId={route.collectionId} />
    );
  }

  if (error) {
    return (
      <Screen>
        <CenterState>
          <ErrorBanner>{error.message}</ErrorBanner>
          {/* Обрыв связи — не приговор: дать нажать «ещё раз» дешевле, чем
              заставлять закрывать и открывать приложение. */}
          {error.code === 'NETWORK' && (
            <button className="btn btn--primary" onClick={() => void loadMe()}>
              Попробовать ещё раз
            </button>
          )}
          {!isInsideTelegram() && (
            <p className="muted small">
              Приложение открыто вне Telegram. Для локальной отладки сгенерируй подписанную
              initData: <code>npm run gen:initdata -- --write</code> — она попадёт в{' '}
              <code>VITE_DEV_INIT_DATA</code> в .env.
            </p>
          )}
        </CenterState>
      </Screen>
    );
  }

  if (!me) {
    return (
      <Screen>
        <CenterState>
          <Spinner />
        </CenterState>
      </Screen>
    );
  }

  // Статистика — только своя и только автору: сервер отдаёт её по тем же
  // правилам, что и правку подборки, поэтому отдельной проверки здесь нет.
  if (route.name === 'stats') {
    return <StatsView collectionId={route.collectionId} />;
  }

  if (route.name === 'editor') {
    return (
      <EditorView
        me={me}
        collectionId={route.collectionId}
        onDone={() => setRoute(rootRoute.current)}
        onPreview={(id) => setRoute({ name: 'viewer', collectionId: id })}
      />
    );
  }

  return (
    <HomeView
      me={me}
      onMeChange={setMe}
      onCreate={() => setRoute({ name: 'editor' })}
      onOpen={(id) => setRoute({ name: 'editor', collectionId: id })}
      onStats={(id) => setRoute({ name: 'stats', collectionId: id })}
    />
  );
}
